// Tiny inline preview of the Mass Extinction worm cosmetic. Renders two
// gold segmented worms wriggling in a Lissajous-style figure-8 path inside
// a small box so the /achievements featured-card can show pilots WHAT
// they'll earn — turning "you can earn this cosmetic" into "watch what
// you'll get". Pure CSS+canvas, no engine bloat: one rAF loop per mount,
// auto-cancelled on unmount.
import { useEffect, useRef } from "react";

export function MassExtinctionPreview({ size = 56 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // Each worm carries its own seed so they're out of phase — looks like
    // a real swarm, not two synchronised dots. Trail lengths intentionally
    // short to keep CPU near zero on long-lived /achievements pages.
    const worms = [
      { phase: 0, hue: "#ffea3a" },
      { phase: Math.PI, hue: "#ffd700" },
    ].map((w) => ({ ...w, segments: [] }));

    let raf = 0;
    let alive = true;
    const loop = (now) => {
      if (!alive) return;
      ctx.clearRect(0, 0, size, size);
      const cx = size / 2, cy = size / 2;
      const r = size * 0.28;
      for (const w of worms) {
        // Lissajous: 1:2 ratio gives a tight figure-8 inside the box.
        const t = now * 0.0028 + w.phase;
        const x = cx + Math.sin(t) * r;
        const y = cy + Math.sin(t * 2) * r * 0.6;
        w.segments.unshift({ x, y });
        if (w.segments.length > 8) w.segments.length = 8;
        for (let si = w.segments.length - 1; si >= 0; si--) {
          const seg = w.segments[si];
          const fade = 1 - si / Math.max(1, w.segments.length);
          const dotR = 1.2 + 1.4 * fade;
          ctx.globalAlpha = 0.2 + 0.7 * fade;
          ctx.fillStyle = w.hue;
          ctx.beginPath();
          ctx.arc(seg.x, seg.y, dotR, 0, Math.PI * 2);
          ctx.fill();
        }
        // Bright head with subtle glow halo so it reads at thumbnail size.
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 6;
        ctx.shadowColor = w.hue;
        ctx.fillStyle = "#fffadc";
        ctx.beginPath();
        ctx.arc(w.segments[0].x, w.segments[0].y, 2.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, [size]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="mass-extinction-preview"
      className="border border-[#ffea3a]/40 bg-black/40"
      style={{ boxShadow: "0 0 10px rgba(255,234,58,0.35)" }}
    />
  );
}
